import {
  Grid,
  TextField,
  Typography,
  Button,
  Box,
  FormControlLabel,
  FormHelperText,
  Radio,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  RadioGroup,
  SelectChangeEvent,
  Checkbox,
} from "@mui/material";

import { useEffect, useState } from "react";
import useFetchData from "../../../hooks/useFetchData";
import { ContractorFormProps } from "./type";
import { useNavigate, useParams } from "react-router-dom";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import {
  emailErrorMessage,
  emailRegex,
  phoneNumberErrorMessage,
  phoneNumberRegex,
} from "../../../utils/ValidationHelper";
import RequiredLabel from "../../atoms/RequiredLabel";
import ConfirmDialog from "../../molecules/ConfirmDialog";
import React from "react";

export type Building = {
  autoBuildingId: number;
  buildingId: string;
  buildingManagementId: string;
  buildingName: string;
  buildingAddress: string;
};

const ContractorForm = ({
  fetchedData,
  setFetchedData,
  isNew, // 新規作成かどうかを判定するためのprops
  setSnackbarOpen,
  setAlertSeverity,
  setSnackbarMessage,
  isEnabled,
  setIsEnabled,
}: ContractorFormProps & { isNew: boolean } & {
  isEnabled: boolean | undefined;
} & {
  setIsEnabled: React.Dispatch<React.SetStateAction<boolean | undefined>>;
}) => {
  const [buildings, setBuildings] = useState<Building[] | undefined>([]);
  const { autoBuildingId, autoContractId } = useParams(); //URLパラメータから取得
  const url =
    process.env.REACT_APP_API_ENDPOINT + "/buildings/" + autoBuildingId;
  // call showBuilding API
  const { data, isLoading, error } = useFetchData<Building>(url);

  useEffect(() => {
    if (data) {
      setBuildings(Array.isArray(data) ? data : [data]);
    }
  }, [data]);

  // 新規登録時のデータ
  const [contractId, setContractId] = useState<string>("");
  const [roomNumber, setRoomNumber] = useState<string>("");
  const [contractorName, setContractorName] = useState<string>("");
  const [contractManagementId, setContractManagementId] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [memo, setMemo] = useState<string>("");
  // "手動でドアを開ける"ボタンに関する状態
  const [isProximityKeyContract, setIsProximityKeyContract] =
    useState<boolean>(false);
  // 時限鍵の利用に関する状態
  const [canSetKeyExpiration, setCanSetKeyExpiration] =
    useState<boolean>(false);

  // 確認ダイアログ
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();

  const CHARACTER_LIMIT = 50; // 文字数制限を定義

  // 各フィールドが変更されたかどうかを追跡する状態
  const [isTouched, setIsTouched] = useState({
    contractorName: false,
    contractManagementId: false,
    phoneNumber: false,
    roomNumber: false,
    email: false,
    memo: false,
  });

  // バリデーションエラーの状態
  const [validationErrors, setValidationErrors] = useState({
    contractorName: "",
    contractManagementId: "",
    phoneNumber: "",
    roomNumber: "",
    email: "",
    memo: "",
  });

  // 入力が変更されたときのハンドラ
  const handleBlur = (field: string) => {
    setIsTouched({
      ...isTouched,
      [field]: true,
    });
  };

  // バリデーションロジック
  useEffect(() => {
    const errors = {
      contractorName:
        isTouched.contractorName &&
        ((!fetchedData?.contractorName &&
          contractorName.length > CHARACTER_LIMIT) ||
          (fetchedData?.contractorName &&
            contractorName !== fetchedData.contractorName &&
            ((isNew && contractorName.length > CHARACTER_LIMIT) ||
              (!isNew && fetchedData.contractorName.length > CHARACTER_LIMIT))))
          ? "契約者氏名は50文字以内で入力してください"
          : "",
      contractManagementId:
        isTouched.contractManagementId &&
        ((!fetchedData?.contractManagementId &&
          contractManagementId.length > CHARACTER_LIMIT) ||
          (fetchedData?.contractManagementId &&
            contractManagementId !== fetchedData.contractorName &&
            ((isNew && contractManagementId.length > CHARACTER_LIMIT) ||
              (!isNew &&
                fetchedData.contractManagementId.length > CHARACTER_LIMIT))))
          ? "契約管理IDは50文字以内で入力してください"
          : "",
      phoneNumber:
        isTouched.phoneNumber &&
        ((isNew &&
          phoneNumber !== "" &&
          !phoneNumber.match(phoneNumberRegex)) ||
          (!isNew &&
            fetchedData &&
            fetchedData.phoneNumber &&
            !fetchedData.phoneNumber.match(phoneNumberRegex)))
          ? phoneNumberErrorMessage
          : "",
      email:
        isTouched.email &&
        ((isNew && email !== "" && !emailRegex.test(email)) ||
          (!isNew &&
            fetchedData &&
            fetchedData.email &&
            !emailRegex.test(fetchedData.email)))
          ? emailErrorMessage
          : "",
      roomNumber:
        isTouched.roomNumber &&
        ((!fetchedData?.roomNumber && roomNumber.length > CHARACTER_LIMIT) ||
          (fetchedData?.roomNumber &&
            roomNumber !== fetchedData.roomNumber &&
            ((isNew && roomNumber.length > CHARACTER_LIMIT) ||
              (!isNew && fetchedData.roomNumber.length > CHARACTER_LIMIT))))
          ? "部屋番号は50文字以内で入力してください"
          : "",
      memo:
        isTouched.memo &&
        ((!fetchedData?.memo && memo.length > CHARACTER_LIMIT) ||
          (fetchedData?.memo &&
            memo !== fetchedData.memo &&
            ((isNew && memo.length > CHARACTER_LIMIT) ||
              (!isNew && fetchedData.memo.length > CHARACTER_LIMIT))))
          ? "メモは50文字以内で入力してください"
          : "",
    };
    setValidationErrors(errors);
  }, [
    contractorName,
    fetchedData?.contractorName,
    fetchedData?.email,
    email,
    fetchedData?.phoneNumber,
    phoneNumber,
    roomNumber,
    fetchedData?.roomNumber,
    memo,
    fetchedData?.memo,
    isNew,
    isTouched,
  ]);

  const handleCancelClick = () => {
    console.log(`キャンセルがクリックされました`);
    navigate(-1);
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    if (!roomNumber && !fetchedData?.roomNumber) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        roomNumber: "部屋番号は必須です",
      }));
      return; // エラーがあるため、保存操作を中断
    }

    // エラーがある場合、保存を阻止
    if (Object.values(validationErrors).some((error) => error !== "")) {
      setSnackbarMessage("入力エラーがあります。修正してください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();

    if (isNew) {
      // 新規の場合の保存処理
      const postData = {
        contractManagementId,
        roomNumber,
        isProximityKeyContract,
        canSetKeyExpiration,
        contractorName,
        phoneNumber,
        email,
        memo,
      };

      const postUrl =
        process.env.REACT_APP_API_ENDPOINT +
        `/buildings/${autoBuildingId}/contracts`; // POSTリクエストを送信するURL

      // call storeContract API
      const response = await fetch(postUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: jwtToken,
        },
        body: JSON.stringify(postData),
      });

      if (!response.ok) {
        console.error("Error in posting 契約情報"); // エラーメッセージを表示します
        return;
      }

      console.log("POSTデータ" + JSON.stringify(postData));

      const responseData = await response.json(); // レスポンスをJSON形式で取得します
      console.log("レスポンスデータ：" + JSON.stringify(responseData));

      const autoContractId = responseData.autoContractId; // レスポンスからautoContractIdを取得します

      navigate(
        `/buildings/${autoBuildingId}/contractors/${autoContractId}/edit`
      );
    } else {
      const patchData = {
        roomNumber: fetchedData?.roomNumber,
        isProximityKeyContract: fetchedData?.isProximityKeyContract,
        canSetKeyExpiration: fetchedData?.canSetKeyExpiration,
        contractorName: fetchedData?.contractorName,
        contractManagementId: fetchedData?.contractManagementId,
        phoneNumber: fetchedData?.phoneNumber,
        email: fetchedData?.email,
        memo: fetchedData?.memo,
        isEnabled: fetchedData?.isEnabled,
      };

      const patchUrl =
        process.env.REACT_APP_API_ENDPOINT +
        `/buildings/${autoBuildingId}/contracts/${autoContractId}`; // PATCHリクエストを送信するURL

      // call updateContract API
      const response = await fetch(patchUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: jwtToken,
        },
        body: JSON.stringify(patchData),
      });

      if (!response.ok) {
        console.error("Error in updating 契約情報"); // エラーメッセージを表示します
        return;
      }

      // エラーがなかったら更新する
      setIsEnabled(fetchedData?.isEnabled);

      const responseData = await response.json(); // レスポンスをJSON形式で取得します
      console.log("レスポンスデータ：" + JSON.stringify(responseData));

      setSnackbarMessage("契約情報が更新されました。");
      setAlertSeverity("success");
      setSnackbarOpen(true);
    }
    setIsSubmitting(false);
  };

  // 削除ボタンのクリックイベントハンドラ
  const handleDeleteButtonClick = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();
    if (!autoBuildingId || !autoContractId) {
      setSnackbarMessage("Error: Building ID or Contract ID is missing");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      const url =
        process.env.REACT_APP_API_ENDPOINT +
        `/buildings/${autoBuildingId}/contracts/${autoContractId}`;
      // call deleteContract API
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: jwtToken,
        },
      });

      if (!response.ok) throw new Error("API Request failed");

      const data = await response.json();
      setSnackbarMessage(data.message);
      setAlertSeverity("success");
      setSnackbarOpen(true);

      // 成功メッセージを表示してから3秒後に遷移
      setTimeout(() => {
        navigate(`/buildings/${autoBuildingId}/contractors`);
      }, 3000);
    } catch (error) {
      console.error(error);
      setSnackbarMessage("削除に失敗しました。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={8}>
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row" colSpan={2}>
                  <Typography variant="h6">契約情報</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>契約システムID:</TableCell>
                <TableCell>
                  {isNew ? (
                    <Typography>（自動生成）</Typography>
                  ) : (
                    <Typography>{fetchedData?.contractId}</Typography>
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>物件システムID:</TableCell>
                <TableCell>
                  <Typography>
                    {fetchedData?.building
                      ? fetchedData.building.buildingId
                      : buildings && buildings.length > 0
                      ? buildings[0].buildingId
                      : ""}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>取付先コード（物件管理ID）:</TableCell>
                <TableCell>
                  <Typography>
                    {fetchedData?.building
                      ? fetchedData.building.buildingManagementId
                      : buildings && buildings.length > 0
                      ? buildings[0].buildingManagementId
                      : ""}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>取付先名（物件名称）:</TableCell>
                <TableCell>
                  <Typography>
                    {fetchedData?.building
                      ? fetchedData.building.buildingName
                      : buildings && buildings.length > 0
                      ? buildings[0].buildingName
                      : ""}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>物件住所:</TableCell>
                <TableCell>
                  <Typography>
                    {fetchedData?.building
                      ? fetchedData.building.buildingAddress
                      : buildings && buildings.length > 0
                      ? buildings[0].buildingAddress
                      : ""}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  部屋番号:
                  <RequiredLabel />
                </TableCell>
                <TableCell>
                  {isNew ? (
                    <TextField
                      size="small"
                      value={roomNumber}
                      onChange={(e) => setRoomNumber(e.target.value)}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("roomNumber")}
                      helperText={validationErrors.roomNumber}
                      error={validationErrors.roomNumber !== ""}
                    />
                  ) : (
                    <TextField
                      size="small"
                      value={fetchedData?.roomNumber}
                      onChange={(e) => {
                        if (fetchedData) {
                          setFetchedData({
                            ...fetchedData,
                            roomNumber: e.target.value,
                          });
                        }
                      }}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("roomNumber")}
                      helperText={validationErrors.roomNumber}
                      error={validationErrors.roomNumber !== ""}
                    />
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>「手動でドアを開ける」ボタン:</TableCell>
                <TableCell>
                  {isNew ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isProximityKeyContract}
                          onChange={(e) =>
                            setIsProximityKeyContract(e.target.checked)
                          }
                        />
                      }
                      label="無効"
                    />
                  ) : (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={fetchedData?.isProximityKeyContract || false}
                          disabled
                        />
                      }
                      label="無効"
                    />
                  )}
                </TableCell>
              </TableRow>

              {/* 時限鍵を使用する */}
              <TableRow>
                <TableCell>時限鍵の設定:</TableCell>
                <TableCell>
                  {isNew ? (
                    // 新規作成時のセレクター
                    <FormControl fullWidth>
                      <RadioGroup
                        value={canSetKeyExpiration ? "利用する" : "利用しない"}
                        onChange={(e) =>
                          setCanSetKeyExpiration(e.target.value === "利用する")
                        }
                      >
                        <FormControlLabel
                          value="利用する"
                          control={<Radio />}
                          label="利用する"
                        />
                        <FormControlLabel
                          value="利用しない"
                          control={<Radio />}
                          label="利用しない"
                        />
                      </RadioGroup>
                    </FormControl>
                  ) : // 編集時の条件分岐
                  fetchedData?.canSetKeyExpiration === false ? (
                    <Typography>利用しない</Typography>
                  ) : (
                    <FormControl fullWidth>
                      <RadioGroup
                        value={
                          fetchedData?.canSetKeyExpiration
                            ? "利用する"
                            : "利用しない"
                        }
                        onChange={(e: SelectChangeEvent) => {
                          if (fetchedData) {
                            setFetchedData({
                              ...fetchedData,
                              canSetKeyExpiration:
                                e.target.value === "利用する",
                            });
                          }
                        }}
                      >
                        <FormControlLabel
                          value="利用する"
                          control={<Radio disabled />}
                          label="利用する"
                        />
                        <FormControlLabel
                          value="利用しない"
                          control={<Radio disabled />}
                          label="利用しない"
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>契約管理ID:</TableCell>
                <TableCell>
                  {isNew ? (
                    <TextField
                      size="small"
                      value={contractManagementId}
                      onChange={(e) => setContractManagementId(e.target.value)}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("contractManagementId")}
                      helperText={validationErrors.contractManagementId}
                      error={validationErrors.contractManagementId !== ""}
                    />
                  ) : (
                    <TextField
                      size="small"
                      value={fetchedData?.contractManagementId}
                      onChange={(e) => {
                        if (fetchedData) {
                          setFetchedData({
                            ...fetchedData,
                            contractManagementId: e.target.value,
                          });
                        }
                      }}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("contractManagementId")}
                      helperText={validationErrors.contractManagementId}
                      error={validationErrors.contractManagementId !== ""}
                    />
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>契約者氏名:</TableCell>
                <TableCell>
                  {isNew ? (
                    <TextField
                      size="small"
                      value={contractorName}
                      onChange={(e) => setContractorName(e.target.value)}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("contractorName")}
                      helperText={validationErrors.contractorName}
                      error={validationErrors.contractorName !== ""}
                    />
                  ) : (
                    <TextField
                      size="small"
                      value={fetchedData?.contractorName}
                      onChange={(e) => {
                        if (fetchedData) {
                          setFetchedData({
                            ...fetchedData,
                            contractorName: e.target.value,
                          });
                        }
                      }}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("contractorName")}
                      helperText={validationErrors.contractorName}
                      error={validationErrors.contractorName !== ""}
                    />
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>電話番号:</TableCell>
                <TableCell>
                  {isNew ? (
                    <TextField
                      size="small"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("phoneNumber")}
                      helperText={validationErrors.phoneNumber}
                      error={validationErrors.phoneNumber !== ""}
                    />
                  ) : (
                    <TextField
                      size="small"
                      value={fetchedData?.phoneNumber}
                      onChange={(e) => {
                        if (fetchedData) {
                          setFetchedData({
                            ...fetchedData,
                            phoneNumber: e.target.value,
                          });
                        }
                      }}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("phoneNumber")}
                      helperText={validationErrors.phoneNumber}
                      error={validationErrors.phoneNumber !== ""}
                    />
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>メールアドレス:</TableCell>
                <TableCell>
                  {isNew ? (
                    <TextField
                      size="small"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("email")}
                      helperText={validationErrors.email}
                      error={validationErrors.email !== ""}
                    />
                  ) : (
                    <TextField
                      size="small"
                      value={fetchedData?.email}
                      onChange={(e) => {
                        if (fetchedData) {
                          setFetchedData({
                            ...fetchedData,
                            email: e.target.value,
                          });
                        }
                      }}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("email")}
                      helperText={validationErrors.email}
                      error={validationErrors.email !== ""}
                    />
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>メモ:</TableCell>
                <TableCell>
                  {isNew ? (
                    <TextField
                      size="small"
                      value={memo}
                      onChange={(e) => setMemo(e.target.value)}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("memo")}
                      helperText={validationErrors.memo}
                      error={validationErrors.memo !== ""}
                    />
                  ) : (
                    <TextField
                      size="small"
                      value={fetchedData?.memo}
                      onChange={(e) => {
                        if (fetchedData) {
                          setFetchedData({
                            ...fetchedData,
                            memo: e.target.value,
                          });
                        }
                      }}
                      style={{ width: "50%" }}
                      onBlur={() => handleBlur("memo")}
                      helperText={validationErrors.memo}
                      error={validationErrors.memo !== ""}
                    />
                  )}
                </TableCell>
              </TableRow>
              {!isNew && (
                <>
                  <TableRow>
                    <TableCell>状態:</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <FormControlLabel
                          value="有効"
                          control={
                            <Radio
                              checked={fetchedData?.isEnabled === true}
                              onChange={(e) => {
                                if (fetchedData) {
                                  setFetchedData({
                                    ...fetchedData,
                                    isEnabled: e.target.value === "有効",
                                  });
                                }
                              }}
                            />
                          }
                          label="有効"
                        />
                        <FormControlLabel
                          value="無効"
                          control={
                            <Radio
                              checked={fetchedData?.isEnabled === false}
                              onChange={(e) => {
                                if (fetchedData) {
                                  setFetchedData({
                                    ...fetchedData,
                                    isEnabled: e.target.value === "有効",
                                  });
                                }
                              }}
                            />
                          }
                          label="無効"
                        />
                        <FormHelperText>
                          無効を選択すると発行済み鍵一覧の鍵は利用不可になります。
                        </FormHelperText>
                      </Box>
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box mt={2} display="flex" justifyContent="flex-start">
          {isNew ? (
            <>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancelClick}
              >
                キャンセル
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={(e) => {
                  if (isSubmitting) return;
                  handleSave();
                }}
                disabled={isSubmitting}
                sx={{ marginLeft: 1 }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    登録中...
                  </Box>
                ) : (
                  "登録・鍵発行"
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  if (isDeleting) return;
                  if (fetchedData?.isEnabled) {
                    setOpenConfirm(true);
                  } else {
                    setOpenConfirm(true);
                  }
                }}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    削除中...
                  </Box>
                ) : (
                  "契約削除"
                )}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={(e) => {
                  if (isSubmitting) return;
                  handleSave();
                }}
                disabled={isSubmitting}
                sx={{ marginLeft: 1 }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    更新中...
                  </Box>
                ) : (
                  "契約情報更新"
                )}
              </Button>
            </>
          )}
        </Box>
        <ConfirmDialog
          open={openConfirm}
          onClose={() => {
            setOpenConfirm(false);
          }}
          onConfirm={() => {
            if (!fetchedData?.isEnabled) {
              handleDeleteButtonClick();
            }
            setOpenConfirm(false);
          }}
          title="確認"
          description={
            fetchedData?.isEnabled
              ? "現在契約が「有効」です。契約削除する場合は、最初に「無効」にしてください。"
              : "一度削除した契約者は元に戻せません。本当に削除しますか?"
          }
        />
      </Grid>
    </Grid>
  );
};

export default ContractorForm;
